/**
 * EXTINCTRUM — entry point.
 *
 * The landing page paints instantly with no WebGL work. The game is not
 * constructed until the visitor presses PLAY (or a harness auto-boots — see
 * `shouldAutoBoot`). PLAY shows a loading bar while `Game.init()` builds the
 * floodplain, then drops straight into play.
 */
import './style.css';
import { registerSW } from 'virtual:pwa-register';
import { Landing } from './ui/Landing';
import { detectedTier } from './engine/Quality';
import { readIdentity, type PlayerIdentity } from './game/Identity';
import type { QualityTier } from './engine/Quality';
import type { Game } from './game/Game';
import { telemetry } from './systems/Telemetry';

const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    showUpdateBanner(() => updateSW(true));
  },
});

/**
 * `autoUpdate` swaps the service worker silently for a *new* visit — a tab
 * that has been sitting on the landing page keeps running the old JS until
 * something tells it to reload. `onNeedRefresh` is that signal: a new SW has
 * installed and is waiting to take over. The banner is the only UI for it —
 * reload is the whole action, there is no changelog copy to write per visit.
 */
function showUpdateBanner(reload: () => void): void {
  const bar = document.createElement('div');
  bar.className = 'ls-update-banner';
  bar.setAttribute('role', 'status');
  const label = document.createElement('span');
  label.textContent = 'A new version is available.';
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = 'Reload';
  btn.addEventListener('click', reload);
  bar.append(label, btn);
  document.body.appendChild(bar);
}

const canvas = document.querySelector<HTMLCanvasElement>('#game');
if (!canvas) throw new Error('Missing #game canvas');
const gameCanvas = canvas;

// C7.3 — telemetry is on for shipped builds; local dev/harness runs stay quiet
// unless explicitly enabled with `?telemetry=1`. See `src/systems/Telemetry.ts`.
if (!import.meta.env.PROD && !location.search.includes('telemetry=1')) telemetry.disable();
telemetry.installCrashCapture();
// A lost WebGL context is a recoverable-but-notable failure; report it. WebGPU
// device loss surfaces through the uncaught-error handler + the unload beacon.
gameCanvas.addEventListener('webglcontextlost', () => {
  telemetry.track('context_lost', {
    backend: String((globalThis as Record<string, unknown>).__EXT_BACKEND__ ?? 'unknown'),
  });
});
// Flush the queue on tab teardown so a crash that kills the page still reports
// the events captured up to that point.
window.addEventListener('pagehide', () => telemetry.flushBeacon());
document.addEventListener('visibilitychange', () => {
  if (document.hidden) telemetry.flushBeacon();
});

// Coarse pointer ⇒ touch controls line on the landing screen. Matches the
// heuristic Input.ts uses; Landing only needs it for copy.
const isTouch =
  typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;

/**
 * Harnesses drive the game with URL params (`?quality=`, `?doom=`, `?losdebug=`…)
 * and expect it to boot on load with no landing to click through. So: any query
 * string, or an explicit `?autoplay=1`, skips the landing and boots immediately.
 * A bare `/` visit — a real visitor — gets the landing.
 */
function shouldAutoBoot(): boolean {
  return location.search.length > 1;
}

/**
 * The game lives at `#play` so the browser back button returns to the landing.
 * We can't tear the running game down cleanly back to the landing, so leaving
 * `#play` (back button, or a nav-brand click) just reloads to `/`.
 */
const PLAY_ROUTE = '#play';
const atPlayRoute = () => location.hash === PLAY_ROUTE;

const detected = detectedTier();
const landing = new Landing(document.body, isTouch, detected);

let game: Game | null = null;
let booting = false;

/**
 * P2.4 — the game (and with it all of three.webgpu) is a separate chunk.
 *
 * The landing is genuinely Three-free: nothing under `ui/landing/`, `Quality`,
 * `Identity` or `Telemetry` imports `three`. A single static `import { Game }`
 * here was the only reason a visitor who never presses PLAY downloaded the
 * whole renderer — 1,265 KiB of eager JS to paint a text page.
 *
 * Deferring it alone would move that cost onto PLAY, where it lands in front of
 * an already expensive `Game.init()`. So the chunk is *prefetched* once the
 * landing is interactive: an idle visitor who reads the page pays nothing on
 * first paint and finds it warm at PLAY, and only an instant-PLAY visitor waits
 * on the fetch — which is the same download they used to make before paint.
 *
 * The promise is memoised, so PLAY arriving mid-prefetch joins the in-flight
 * request rather than starting a second one.
 */
let gameModule: Promise<typeof import('./game/Game')> | null = null;
function loadGameModule(): Promise<typeof import('./game/Game')> {
  gameModule ??= import('./game/Game');
  return gameModule;
}

/**
 * Warm the chunk without competing with the landing's own first paint.
 * `requestIdleCallback` is not in Safari's older releases, hence the timeout
 * fallback; either way a failure here is silent — `loadGameModule()` retries at
 * PLAY and a rejected prefetch must not surface as an unhandled rejection.
 */
function prefetchGameModule(): void {
  const go = () => { void loadGameModule().catch(() => { gameModule = null; }); };
  if (typeof requestIdleCallback === 'function') requestIdleCallback(go, { timeout: 3000 });
  else setTimeout(go, 1200);
}

async function bootGame(
  tier: QualityTier,
  identity: PlayerIdentity,
  autoBegin: boolean,
): Promise<void> {
  if (booting || game) return;
  booting = true;
  landing.markLoading();

  // The chunk fetch is inside the try: on a flaky connection a failed import()
  // is exactly as fatal as a failed init, and the visitor deserves the same
  // `markError()` rather than a silent dead PLAY button.
  let g: Game | undefined;
  try {
    const { Game } = await loadGameModule();
    g = new Game(gameCanvas);
    await g.init(tier, identity);
  } catch (err) {
    console.error('[EXTINCTRUM] failed to boot', err);
    telemetry.track('boot_failed', {
      message: String(err).slice(0, 300),
      stack: err instanceof Error && typeof err.stack === 'string' ? err.stack.slice(0, 1200) : '',
      tier,
    });
    // Undefined when the chunk fetch itself failed, so there is nothing built
    // to tear down; drop the memo so a retry re-fetches rather than replaying
    // the rejection.
    g?.dispose();
    if (!g) gameModule = null;
    booting = false;
    landing.markError();
    return;
  }

  game = g;
  booting = false;
  game.start();
  landing.markReady();
  // C7.3 — a successful boot is the first real player signal. Context is set
  // here (backend is only known once the renderer is up) so the first flush
  // carries the device/identity info alongside the first events.
  telemetry.setContext({
    tier,
    backend: String((globalThis as Record<string, unknown>).__EXT_BACKEND__ ?? 'unknown'),
    mobile: isTouch,
    voice: identity.voice,
    nameLength: identity.name.length,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  });
  telemetry.track('boot_success');

  if (autoBegin) {
    // The landing (and its loading bar) stays up for the whole of
    // `Game.init()` so PLAY does not drop the visitor onto a frozen frame with
    // no feedback. `markError()` above also relies on it still being visible on
    // a failed boot.
    //
    // It also stays up for the first few *presented* frames, which cost several
    // seconds of GPU pipeline work no JS warmup can absorb (see
    // `Game.renderFirstFrame`). Hiding before that is what put a ~7 s freeze on
    // the player with the landing already gone. `begin()` is deferred until
    // after it too, so the run — and the raptor — start on a warm frame.
    landing.markWarming('Locating the power core…');
    await game.renderFirstFrame();
    // Await the fade, don't just start it: `.hidden` is a 0.55 s opacity
    // transition, so calling `begin()` on the next line started the opening
    // monologue — caption and VO — over a landing page still on screen (C7.2a).
    await landing.hide();
    game.begin();
  }
}

function resolvedTier(): QualityTier {
  const c = landing.selectedChoice();
  return c === 'auto' ? detected : c;
}

/**
 * The landing owns the live control, but a harness boots without ever
 * constructing one (`shouldAutoBoot`) — so fall back to storage, which yields
 * the default identity when nothing was ever chosen.
 */
function resolvedIdentity(): PlayerIdentity {
  return landing ? landing.selectedIdentity() : readIdentity();
}

// PLAY: push the #play route. The hashchange handler does the actual boot, so
// the history entry exists and back returns here.
landing.onBegin = () => {
  if (atPlayRoute()) void bootGame(resolvedTier(), resolvedIdentity(), true);
  else location.hash = PLAY_ROUTE;
};

// Back/forward across the #play boundary. Entering #play boots the game;
// leaving it (back button) reloads to the clean landing.
window.addEventListener('hashchange', () => {
  if (atPlayRoute()) {
    void bootGame(resolvedTier(), resolvedIdentity(), true);
  } else if (game || booting) {
    location.reload();
  }
});

if (shouldAutoBoot()) {
  // Harness path: no landing gesture, boot straight in with no bar. The fade
  // is deliberately not awaited — it runs out during `Game.init()`, which is
  // far longer, so `begin()` lands well after the landing is gone either way.
  void landing.hide();
  void bootGame(resolvedTier(), resolvedIdentity(), true);
} else if (atPlayRoute()) {
  // Direct link to #play: keep the (instantly-painted) landing up with its
  // loading bar through `Game.init()`; `bootGame` hides it on ready.
  void bootGame(resolvedTier(), resolvedIdentity(), true);
} else {
  // The only path that actually stops on the landing. The other two are already
  // booting, so they hold the chunk request and must not be given a second one.
  prefetchGameModule();
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => game?.dispose());
}
